import { useEffect, useMemo, useState } from "react";
import type { LocalStore } from "@onchainreps/storage";
import { DrillBoard } from "./DrillBoard";
import type { DrillItem } from "../lib/drills";
import { puzzleDrill } from "../lib/drills";
import {
  loadPuzzleIndex,
  loadShard,
  selectPuzzles,
  shardForRating,
  PUZZLE_THEME_LABELS,
  type PuzzleIndex,
} from "../lib/puzzles";
import { useDrillSession } from "../lib/useDrillSession";

interface TacticsTrainerProps {
  store: LocalStore | null;
}

const SESSION_LENGTH = 10;

export function TacticsTrainer({ store }: TacticsTrainerProps) {
  const [index, setIndex] = useState<PuzzleIndex | null>(null);
  const [rating, setRating] = useState(1200);
  const [themes, setThemes] = useState<string[]>([]);
  const [drills, setDrills] = useState<DrillItem[]>([]);
  const [phase, setPhase] = useState<"setup" | "session" | "summary">("setup");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const session = useDrillSession(drills, store);

  useEffect(() => {
    let active = true;
    void loadPuzzleIndex()
      .then((loaded) => {
        if (active) setIndex(loaded);
      })
      .catch((cause: unknown) => {
        if (active) setError(cause instanceof Error ? cause.message : "Could not load puzzles.");
      });
    return () => {
      active = false;
    };
  }, []);

  const availableThemes = useMemo(() => {
    if (!index) return [];
    const counts = new Map(index.themes.map((entry) => [entry.theme, entry.count]));
    return PUZZLE_THEME_LABELS.filter(([theme]) => (counts.get(theme) ?? 0) > 200);
  }, [index]);

  const start = async () => {
    if (!index) return;
    setBusy(true);
    setError(null);
    try {
      const shard = shardForRating(index, rating);
      const puzzles = await loadShard(shard.file);
      const exclude = new Set<string>();
      const picked = selectPuzzles(puzzles, {
        count: SESSION_LENGTH,
        rating,
        themes,
        exclude,
      });
      setDrills(
        picked.map((puzzle) =>
          puzzleDrill({
            id: puzzle.i,
            fen: puzzle.f,
            moves: puzzle.m,
            rating: puzzle.r,
            themes: puzzle.t,
          }),
        ),
      );
      setPhase("session");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not start the session.");
    } finally {
      setBusy(false);
    }
  };

  const toggleTheme = (theme: string) => {
    setThemes((current) =>
      current.includes(theme) ? current.filter((item) => item !== theme) : [...current, theme],
    );
  };

  if (phase === "session" && session.current) {
    return (
      <div className="trainer">
        <div className="trainer__bar">
          <button className="btn btn--inline" onClick={() => setPhase("setup")}>
            Back
          </button>
          <span className="trainer__count">
            {session.index + 1} / {session.total}
          </span>
          <span className="trainer__streak">
            Streak {session.streak} · Best {session.bestStreak}
          </span>
        </div>

        <div className="progress" aria-hidden="true">
          <div
            className="progress__fill"
            style={{ width: `${session.total === 0 ? 0 : (session.index / session.total) * 100}%` }}
          />
        </div>

        <DrillBoard
          key={session.current.id}
          drill={session.current}
          onAnswer={session.answer}
          onNext={() => {
            const last = session.index + 1 >= session.total;
            session.next();
            if (last) setPhase("summary");
          }}
        />
      </div>
    );
  }

  if (phase === "summary") {
    return (
      <div className="trainer">
        <section className="panel">
          <div className="panel__head">
            <h1 className="panel__title">Session complete</h1>
          </div>
          <div className="panel__body">
            <div className="metrics">
              <div className="metric">
                <span className="metric__label">Solved</span>
                <span className="metric__value metric__value--white">{session.solved}</span>
                <span className="metric__note">of {session.total}</span>
              </div>
              <div className="metric">
                <span className="metric__label">Best streak</span>
                <span className="metric__value">{session.bestStreak}</span>
                <span className="metric__note">in a row</span>
              </div>
            </div>
            <div className="btnrow">
              <button className="btn btn--primary" onClick={() => setPhase("setup")}>
                New session
              </button>
              <button
                className="btn btn--ghost"
                onClick={() => {
                  setDrills([]);
                  setPhase("setup");
                }}
              >
                Change filters
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="trainer">
      <section className="panel">
        <div className="panel__head">
          <h1 className="panel__title">Tactics session</h1>
          <span className="chip">
            <span className="chip__dot" />
            {index ? `${index.total.toLocaleString("en-US")} puzzles` : "Loading"}
          </span>
        </div>
        <div className="panel__body">
          <p className="notice">
            Every puzzle comes from the Lichess open database, which is released into the public domain.
            Sessions are served from your own device, so there is no sign-up and no waiting.
          </p>

          <div className="field">
            <label className="field__label" htmlFor="puzzle-rating">
              Your level: {rating}
            </label>
            <input
              id="puzzle-rating"
              className="range"
              type="range"
              min={600}
              max={2400}
              step={100}
              value={rating}
              onChange={(event) => setRating(Number(event.target.value))}
            />
            <span className="field__hint">Puzzles are drawn from the band around your rating.</span>
          </div>

          <div className="field">
            <span className="field__label">Themes</span>
            <div className="chiprow">
              {availableThemes.map(([theme, label]) => (
                <button
                  key={theme}
                  className={`chip chip--toggle${themes.includes(theme) ? " chip--on" : ""}`}
                  onClick={() => toggleTheme(theme)}
                >
                  {label}
                </button>
              ))}
            </div>
            <span className="field__hint">Leave empty for a mixed session.</span>
          </div>

          {error ? <p className="notice notice--error">{error}</p> : null}

          <div className="btnrow">
            <button className="btn btn--primary" onClick={() => void start()} disabled={busy || !index}>
              {busy ? "Loading puzzles" : `Start ${SESSION_LENGTH} puzzles`}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
